import { useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { SOCIAL_ASSET_TYPES, type Brand, type ProjectWithClient, type SocialAssetWithProject } from '../../../shared/types';
import { Modal, SelectField, Seg } from '../../components/primitives';
import { useIsMobile } from '../../lib/useIsMobile';
import { TYPE_LABELS } from '../../lib/socialCalendar';
import { FilterChip } from './chrome';
import { type AssetForm } from './types';

export function AssetModal({
  asset,
  projects,
  onClose,
  onSaved,
}: {
  asset: SocialAssetWithProject | null;
  projects: ProjectWithClient[];
  onClose: () => void;
  onSaved: (saved: SocialAssetWithProject) => void;
}) {
  const [form, setForm] = useState<AssetForm>({
    label: asset?.label ?? '',
    type: asset?.type ?? 'social_made',
    project_id: asset?.project_id ?? null,
    brand: asset?.brand ?? 'ss',
    url: asset?.url ?? '',
    note: asset?.note ?? '',
    // New assets land ready by default (the mockup's pick) — a link to an
    // existing file usually is; flip to needs prep when it still needs work.
    status: asset?.status === 'needs_prep' ? 'needs_prep' : 'ready',
  });
  // Brand pre-fills from the linked project until Henry sets it himself.
  const [brandTouched, setBrandTouched] = useState(asset !== null);
  const isMobile = useIsMobile();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pickProject = (projectId: string | null) => {
    const project = projects.find((p) => p.id === projectId);
    setForm((f) => ({
      ...f,
      project_id: projectId,
      brand: brandTouched ? f.brand : (project?.brand ?? 'ss'),
    }));
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!form.label.trim() || busy) return;
    setBusy(true);
    setError(null);
    const json = {
      label: form.label.trim(),
      type: form.type,
      project_id: form.project_id,
      brand: form.brand,
      url: form.url.trim() || null,
      note: form.note.trim() || null,
      ...(asset === null ? { status: form.status } : {}),
    };
    try {
      const saved = asset === null
        ? await api<{ asset: SocialAssetWithProject }>('/api/admin/social-assets', {
            method: 'POST',
            json,
          })
        : await api<{ asset: SocialAssetWithProject }>(`/api/admin/social-assets/${asset.id}`, {
            method: 'PATCH',
            json,
          });
      onSaved(saved.asset);
      onClose();
    } catch {
      setError('Failed to save asset.');
      setBusy(false);
    }
  };

  return (
    <Modal
      title={asset === null ? 'Add asset' : 'Edit asset'}
      context="SOCIAL"
      onClose={onClose}
      hint="Links only — no uploads"
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => submit()} disabled={!form.label.trim() || busy}>
            {asset === null ? 'Add asset →' : 'Save →'}
          </button>
        </>
      }
    >
      <form className={`form-grid${isMobile ? ' social-asset-form' : ''}`} onSubmit={submit}>
        <div className="f-field span-2">
          <span className="f-label">
            Name <span className="req">*</span>
          </span>
          <input
            className="f-input"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="Hero social cut 9:16, 45s"
            autoFocus
          />
        </div>
        <div className="f-field span-2">
          <span className="f-label">Type</span>
          {isMobile ? (
            <div className="chiprow">
              {SOCIAL_ASSET_TYPES.map((type) => (
                <button
                  type="button"
                  key={type}
                  className={`c${form.type === type ? ' on' : ''}`}
                  aria-pressed={form.type === type}
                  onClick={() => setForm((f) => ({ ...f, type }))}
                >
                  {TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          ) : (
            <div className="social-chiprow">
              {SOCIAL_ASSET_TYPES.map((type) => (
                <FilterChip
                  key={type}
                  label={TYPE_LABELS[type]}
                  on={form.type === type}
                  onClick={() => setForm((f) => ({ ...f, type }))}
                />
              ))}
            </div>
          )}
        </div>
        <div className="f-field span-2">
          <span className="f-label">Source project · optional</span>
          <SelectField
            items={[
              { value: '', label: 'Standalone' },
              ...projects.map((p) => ({ value: p.id, label: `${p.name} · ${p.client_name}` })),
            ]}
            value={form.project_id ?? ''}
            onChange={(id) => pickProject(id || null)}
            placeholder="Standalone · type to link a project…"
            searchable
          />
        </div>
        <div className={`f-field${isMobile && asset !== null ? ' span-2' : ''}`}>
          <span className="f-label">Brand</span>
          <Seg
            options={[
              { value: 'ss' as Brand, label: 'SS' },
              { value: 'hsp' as Brand, label: 'HSP' },
            ]}
            value={form.brand}
            onChange={(brand) => {
              setBrandTouched(true);
              setForm((f) => ({ ...f, brand }));
            }}
          />
          <span className="f-help">Standalone defaults to SS; linked inherits the project</span>
        </div>
        {asset === null && (
          <div className="f-field">
            <span className="f-label">Status</span>
            <Seg
              options={[
                { value: 'needs_prep' as const, label: 'Needs prep' },
                { value: 'ready' as const, label: 'Ready' },
              ]}
              value={form.status}
              onChange={(status) => setForm((f) => ({ ...f, status }))}
            />
          </div>
        )}
        <div className="f-field span-2">
          <span className="f-label">Link · Drive / Frame.io / R2</span>
          <input
            className="f-input mono"
            value={form.url}
            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
            placeholder="https://…"
          />
        </div>
        <div className="f-field span-2">
          <span className="f-label">Note · optional</span>
          <textarea
            className="f-textarea"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="Usage notes, context, caption ideas…"
          />
        </div>
        {error && <p className="error-text span-2">{error}</p>}
      </form>
    </Modal>
  );
}

export function RestrictModal({
  asset,
  onClose,
  onConfirm,
}: {
  asset: SocialAssetWithProject;
  onClose: () => void;
  onConfirm: (note: string | null) => void;
}) {
  const [note, setNote] = useState(asset.restricted_note ?? '');
  return (
    <Modal
      title="Restrict asset"
      context="SOCIAL"
      onClose={onClose}
      mobile="half"
      hint="Remembers the current status for unrestrict"
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => onConfirm(note.trim() || null)}>
            Restrict →
          </button>
        </>
      }
    >
      <div className="f-field">
        <span className="f-label">Why · {asset.label}</span>
        <textarea
          className="f-textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="NDA until public launch, client asked us to hold…"
          autoFocus
        />
        <span className="f-help">To block a whole project, use its "no social use" flag instead</span>
      </div>
    </Modal>
  );
}
